import { createCsvBlob, repairCsvText } from './csvEncoding'

const formatTripExportValue = (val) => {
  if (val === null || val === undefined || val === '') return ''
  if (typeof val === 'number') return val.toString()
  if (typeof val === 'object') return JSON.stringify(val)

  const v = String(val).trim()
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(v)) {
    try {
      return new Date(v).toLocaleString()
    } catch {
      return v
    }
  }

  return v
}

const pad = (n) => String(n).padStart(2, '0')

const fileStamp = () => {
  const d = new Date()
  return `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}_${pad(d.getHours())}${pad(d.getMinutes())}${pad(d.getSeconds())}`
}

const downloadBlob = (blob, name) => {
  const a = document.createElement('a')
  a.href = URL.createObjectURL(blob)
  a.download = name
  a.click()
  URL.revokeObjectURL(a.href)
}

const csvEscape = (value) => `"${repairCsvText(formatTripExportValue(value))}"`

const arrayBufferToBase64 = (buffer) => {
  let binary = ''
  const bytes = new Uint8Array(buffer)

  for (let i = 0; i < bytes.length; i += 1) {
    binary += String.fromCharCode(bytes[i])
  }

  return window.btoa(binary)
}

const loadPdfFont = async (doc, url, fileName, fontName, fontStyle) => {
  const response = await fetch(url)

  if (!response.ok) {
    throw new Error(`PDF font not found: ${url}`)
  }

  const buffer = await response.arrayBuffer()
  const base64 = arrayBufferToBase64(buffer)

  doc.addFileToVFS(fileName, base64)
  doc.addFont(fileName, fontName, fontStyle)
}

const ensureTripPdfFonts = async (doc) => {
  await loadPdfFont(
    doc,
    '/fonts/Roboto-Regular.ttf',
    'Roboto-Regular.ttf',
    'Roboto',
    'normal'
  )

  await loadPdfFont(
    doc,
    '/fonts/Roboto-Bold.ttf',
    'Roboto-Bold.ttf',
    'Roboto',
    'bold'
  )
}

const buildDateLabel = (value) => {
  if (!value) return ''
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return ''
  const hh = String(d.getHours()).padStart(2, '0')
  const mm = String(d.getMinutes()).padStart(2, '0')
  return `${hh}:${mm}`
}

const formatDateOnly = (value) => {
  if (!value) return ''
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return ''

  const day = String(d.getDate()).padStart(2, '0')
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const y = d.getFullYear()

  return `${day}.${m}.${y}`
}

const calculateTripDurationMinutes = (startTimeIso, endTimeIso) => {
  if (!startTimeIso || !endTimeIso) return null
  const start = +new Date(startTimeIso)
  const end = +new Date(endTimeIso)
  if (Number.isNaN(start) || Number.isNaN(end) || end <= start) return null
  return Math.round((end - start) / 60000)
}

const formatDurationHHMM = (minutes) => {
  if (typeof minutes !== 'number' || minutes <= 0) return ''
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  return `${h}:${String(m).padStart(2, '0')}`
}

export const buildTripExportRecords = (trips, vehiclesById, receipts, calculateFuelUsedLiters, t) => {
  return trips.map((trip) => {
    const vehicle = vehiclesById[String(trip?.vehicleId)] || trip?.vehicle

    const fuelUsed = calculateFuelUsedLiters(trip, vehicle ? [vehicle] : [])

const receiptId = trip?.receiptId ?? trip?.receipt_id

let refuel = ''

if (receiptId && Array.isArray(receipts)) {
  const receipt = receipts.find(r => String(r.id) === String(receiptId))
  if (receipt) {
    refuel = `${receipt.units ?? '-'} ${receipt.unit ?? ''}`
  }
}

    const durationMinutesRaw = calculateTripDurationMinutes(
      trip?.startTimeIso,
      trip?.endTimeIso
    )

    const durationMinutes = formatDurationHHMM(durationMinutesRaw)

    let avgSpeedKmh = trip?.avgSpeedKmh ?? null

    if (avgSpeedKmh == null) {
      avgSpeedKmh =
        typeof trip?.distanceKm === 'number' &&
        trip.distanceKm > 0 &&
        typeof durationMinutesRaw === 'number' &&
        durationMinutesRaw > 0
          ? trip.distanceKm / (durationMinutesRaw / 60)
          : null
    }

    if (typeof avgSpeedKmh === 'number') {
      avgSpeedKmh = Number(avgSpeedKmh.toFixed(2))
    }

    const vehicleName = vehicle?.name || vehicle?.model || vehicle?.znacka || ''
    const plate = vehicle?.licensePlate || vehicle?.spz || ''
    const vehicleLabel = vehicleName || '-'

    const model = trip?.model ?? vehicle?.model ?? ''
    const typ_variant_verzia = trip?.typ_variant_verzia ?? vehicle?.variantType ?? ''
    const VIN = trip?.VIN ?? vehicle?.vin ?? ''

    return {
      tripNumber: trip?.tripNumber ?? '',
      vehicleId: vehicleLabel,
      spz: plate,
      model,
      typ_variant_verzia,
      VIN,
      date: formatDateOnly(trip?.date),
      driver: trip?.driver || '',
      startPlace: trip?.startPlace || '',
      endPlace: trip?.endPlace || '',
      odoStart: trip?.odoStart != null ? Number(trip.odoStart) : '',
      odoEnd: trip?.odoEnd != null ? Number(trip.odoEnd) : '',
      distanceKm: trip?.distanceKm != null ? Number(trip.distanceKm) : '',
      fuelUsed: fuelUsed != null ? Number(fuelUsed) : '',
      duration: durationMinutes,
      avgSpeedKmh: avgSpeedKmh != null ? Number(avgSpeedKmh) : '',
      purpose: trip?.purpose || '',
      refuel,
      note: trip?.note || '',
      mode: trip?.mode
  ? t(`TRIPLOG.MODES.${trip.mode.toUpperCase()}`)
  : '',
      startTimeIso: buildDateLabel(trip?.startTimeIso),
      endTimeIso: buildDateLabel(trip?.endTimeIso),
      gpsStart: trip?.gpsStart
        ? `${trip.gpsStart.latitude}, ${trip.gpsStart.longitude}`
        : '',
      gpsEnd: trip?.gpsEnd
        ? `${trip.gpsEnd.latitude}, ${trip.gpsEnd.longitude}`
        : ''
    }
  })
}

const translateTripHeader = (h, t) => {
  if (h === 'tripNumber') return t('TRIPLOG.TRIPS.TABLE.tripNumber')
  if (h === 'spz') return t('vehicle_license_plate')
  if (h === 'duration') return t('TRIPLOG.TRIPS.TABLE.durationMinutes')
  if (h === 'fuelUsed') return t('TRIPLOG.TRIPS.TABLE.fuelUsedLiters')
  if (h === 'refuel') return t('TRIPLOG.TRIPS.TABLE.receiptId')
  return t(`TRIPLOG.TRIPS.TABLE.${h}`, { defaultValue: h })
}

export const exportTripsCSV = (records, headers, t, periodText, vehicleText, odometerHeaderValues = {}) => {
  const isSingleVehicleFiltered = Boolean(odometerHeaderValues?.isSingleVehicleFiltered)
  const multiVehicleHint = t('TRIPLOG.STATISTICS.ODO_SINGLE_VEHICLE_HINT')

  const titleRows = [
    `${t('TRIPLOG.STATISTICS.SHOWN_PERIOD')};"${repairCsvText(periodText)}"`,
    `${t('TRIPLOG.STATISTICS.ODO_PERIOD_START')};"${repairCsvText(isSingleVehicleFiltered ? (odometerHeaderValues?.odoStart ?? '') : multiVehicleHint)}"`,
    `${t('TRIPLOG.STATISTICS.ODO_PERIOD_END')};"${repairCsvText(isSingleVehicleFiltered ? (odometerHeaderValues?.odoEnd ?? '') : multiVehicleHint)}"`,
    `${t('TRIPLOG.STATISTICS.SHOWN_VEHICLE')};"${repairCsvText(vehicleText)}"`,
    ''
  ]

  const header = headers.map((h) => translateTripHeader(h, t)).join(';')

  const rows = records.map((r) =>
    headers.map((h) => csvEscape(r[h])).join(';')
  )

  const csv = [
    ...titleRows,
    header,
    ...rows
  ].join('\n')

  downloadBlob(
    createCsvBlob(csv),
    `trips_${fileStamp()}.csv`
  )
}

export const exportTripsXLS = async (records, headers, t, periodText, vehicleText, odometerHeaderValues = {}) => {
  const XLSX = await import('xlsx-js-style')

  const isSingleVehicleFiltered = Boolean(odometerHeaderValues?.isSingleVehicleFiltered)
  const multiVehicleHint = t('TRIPLOG.STATISTICS.ODO_SINGLE_VEHICLE_HINT')

  const titleRows = [
    [t('TRIPLOG.STATISTICS.SHOWN_PERIOD'), periodText],
    [t('TRIPLOG.STATISTICS.ODO_PERIOD_START'), isSingleVehicleFiltered ? (odometerHeaderValues?.odoStart ?? '') : multiVehicleHint],
    [t('TRIPLOG.STATISTICS.ODO_PERIOD_END'), isSingleVehicleFiltered ? (odometerHeaderValues?.odoEnd ?? '') : multiVehicleHint],
    [t('TRIPLOG.STATISTICS.SHOWN_VEHICLE'), vehicleText],
    []
  ]

  const translatedHeaders = headers.map((h) => translateTripHeader(h, t))
  const dataRows = records.map((record) =>
    headers.map((h) => formatTripExportValue(record[h]) ?? '')
  )

  const worksheetData = [...titleRows, translatedHeaders, ...dataRows]

  const ws = XLSX.utils.aoa_to_sheet(worksheetData)

  ws['!cols'] = headers.map(() => ({ wch: 18 }))

  const headerRowIndex = titleRows.length

  translatedHeaders.forEach((_, colIndex) => {
    const cellAddress = XLSX.utils.encode_cell({
      r: headerRowIndex,
      c: colIndex
    })

    if (!ws[cellAddress]) return

    ws[cellAddress].s = {
      font: {
        name: 'Arial',
        sz: 10,
        bold: true
      },
      fill: {
        fgColor: { rgb: 'F0F0F0' }
      },
      alignment: {
        horizontal: 'left',
        vertical: 'center'
      }
    }
  })

  titleRows.forEach((row, rowIndex) => {
    row.forEach((_, colIndex) => {
      const cellAddress = XLSX.utils.encode_cell({
        r: rowIndex,
        c: colIndex
      })

      if (!ws[cellAddress]) return

      ws[cellAddress].s = {
        font: {
          name: 'Arial',
          sz: 10,
          bold: true
        },
        alignment: {
          horizontal: 'left',
          vertical: 'center'
        }
      }
    })
  })

  dataRows.forEach((row, rowIndex) => {
    row.forEach((_, colIndex) => {
      const cellAddress = XLSX.utils.encode_cell({
        r: headerRowIndex + 1 + rowIndex,
        c: colIndex
      })

      if (!ws[cellAddress]) return

      ws[cellAddress].s = {
        font: {
          name: 'Arial',
          sz: 10
        },
        alignment: {
          horizontal: 'left',
          vertical: 'center'
        }
      }
    })
  })

  const wb = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(wb, ws, 'Trips')

  XLSX.writeFile(wb, `trips_${fileStamp()}.xlsx`)
}

export const exportTripsPDF = async (records, headers, t, periodText, vehicleText, odometerHeaderValues = {}) => {
  const { jsPDF } = await import('jspdf')
  const autoTableModule = await import('jspdf-autotable')
  const autoTable = autoTableModule.default || autoTableModule.autoTable

  const isSingleVehicleFiltered = Boolean(odometerHeaderValues?.isSingleVehicleFiltered)
  const multiVehicleHint = t('TRIPLOG.STATISTICS.ODO_SINGLE_VEHICLE_HINT')

  const translatedHeaders = headers.map((h) => translateTripHeader(h, t))
  const dataRows = records.map((record) =>
    headers.map((h) => formatTripExportValue(record[h]) ?? '')
  )

  const doc = new jsPDF({
    orientation: 'landscape',
    unit: 'pt',
    format: 'a4'
  })

  await ensureTripPdfFonts(doc)

  const marginLeft = 18
  const marginRight = 18
  const pageWidth = doc.internal.pageSize.getWidth()
  const usableWidth = pageWidth - marginLeft - marginRight
  const columnWidth = usableWidth / headers.length

  doc.setFont('Roboto', 'bold')
  doc.setFontSize(14)
  doc.text(t('TRIPLOG.TAB.TRIPS_EXPORT'), marginLeft, 28)

  doc.setFontSize(10)
  doc.text(
    `${t('TRIPLOG.STATISTICS.SHOWN_PERIOD')}: ${periodText}`,
    marginLeft,
    44
  )

  doc.setFontSize(9)
  doc.text(
    `${t('TRIPLOG.STATISTICS.ODO_PERIOD_START')}: ${isSingleVehicleFiltered ? formatTripExportValue(odometerHeaderValues?.odoStart) : multiVehicleHint}`,
    marginLeft,
    58
  )

  doc.text(
    `${t('TRIPLOG.STATISTICS.ODO_PERIOD_END')}: ${isSingleVehicleFiltered ? formatTripExportValue(odometerHeaderValues?.odoEnd) : multiVehicleHint}`,
    marginLeft,
    72
  )

  doc.text(
    `${t('TRIPLOG.STATISTICS.SHOWN_VEHICLE')}: ${vehicleText}`,
    marginLeft,
    86
  )

  autoTable(doc, {
    head: [translatedHeaders],
    body: dataRows,
    startY: 100,
    theme: 'grid',
    margin: {
      top: 18,
      right: marginRight,
      bottom: 18,
      left: marginLeft
    },
    tableWidth: usableWidth,
    styles: {
      font: 'Roboto',
      fontStyle: 'normal',
      fontSize: 6,
      cellPadding: 2,
      overflow: 'linebreak',
      valign: 'middle',
      halign: 'left',
      lineWidth: 0.3,
      lineColor: [0, 0, 0],
      minCellHeight: 10
    },
    headStyles: {
      font: 'Roboto',
      fontStyle: 'bold',
      fontSize: 6,
      fillColor: [240, 240, 240],
      textColor: [0, 0, 0],
      lineWidth: 0.4,
      lineColor: [0, 0, 0]
    },
    bodyStyles: {
      font: 'Roboto',
      fontStyle: 'normal',
      textColor: [0, 0, 0],
      lineWidth: 0.3,
      lineColor: [0, 0, 0]
    },
    columnStyles: headers.reduce((acc, _header, index) => {
      acc[index] = {
        cellWidth: columnWidth
      }

      return acc
    }, {}),
    horizontalPageBreak: false,
    pageBreak: 'auto',
    rowPageBreak: 'avoid'
  })

  downloadBlob(doc.output('blob'), `trips_${fileStamp()}.pdf`)
}

export const exportTripsXML = (records, headers) => {
  const xmlRows = records
    .map((record) => {
      const fields = headers
        .map((h) => `<${h}>${formatTripExportValue(record[h])}</${h}>`)
        .join('')
      return `<trip>${fields}</trip>`
    })
    .join('')

  const xml = `<?xml version="1.0" encoding="UTF-8"?><trips>${xmlRows}</trips>`

  downloadBlob(
    new Blob([xml], { type: 'application/xml;charset=utf-8;' }),
    `trips_${fileStamp()}.xml`
  )
}

export const exportTripsICS = (records) => {
  const events = records
    .map((record) => {
let date = ''

if (record?.date) {
  const parts = record.date.split('-')
  if (parts.length === 3) {
    const [day, month, year] = parts
    date = `${year}${month}${day}T000000`
  }
}

      return `BEGIN:VEVENT
SUMMARY:${record.purpose ?? 'Trip'}
DTSTART:${date}
DESCRIPTION:${record.startPlace ?? ''} → ${record.endPlace ?? ''}
END:VEVENT`
    })
    .join('\n')

  const ics = `BEGIN:VCALENDAR
VERSION:2.0
PRODID:-//EnerqiQ Trips//EN
${events}
END:VCALENDAR`

  downloadBlob(
    new Blob([ics], { type: 'text/calendar;charset=utf-8;' }),
    `trips_${fileStamp()}.ics`
  )
}

export const exportTripsJSON = (records) => {
  downloadBlob(
    new Blob([JSON.stringify(records, null, 2)], {
      type: 'application/json;charset=utf-8;'
    }),
    `trips_${fileStamp()}.json`
  )
}